<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { closeModalById } from '@/utils/modal'
import { useTeamEditionStore } from '@/store/teamEdition'
import { storeToRefs } from 'pinia'

const teamEditionStore = useTeamEditionStore();
const { paymentMethod } = storeToRefs(teamEditionStore);

defineOptions({
  modalId: 'modal-team-order-pending-payment'
})

interface Props {
  packageName?: string
  seatCount?: number
  duration?: number
  durationUnit?: 'year' | 'month'
  originalPrice?: string
  amount?: string
  discount?: string
  qrCodeUrl?: string
  onClose?: () => void
  onPaymentSuccess?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  packageName: '标准版团队',
  seatCount: 0,
  duration: 1,
  durationUnit: 'year',
  originalPrice: '0',
  amount: '0',
  discount: '0',
  qrCodeUrl: ''
})



const show = ref(false)

onMounted(() => {
  show.value = true
})

const closeModal = () => {
  show.value = false
}

const handleAfterClose = () => {
  closeModalById('modal-team-order-pending-payment')
  props.onClose?.()
}

const handleClose = () => {
  closeModal()
}

const durationText = computed(() => {
  return props.durationUnit === 'year' ? '年' : '月'
})
</script>

<template>
  <a-modal
    v-model:open="show"
    wrapClassName="modal-custom team-order-pending-payment-modal"
    :footer="null"
    :maskClosable="false"
    :centered="true"
    :closable="false"
    width="auto"
    :keyboard="false"
    :z-index="1012"
    :afterClose="handleAfterClose"
    :bodyStyle="{
      width: '400px',
      padding: '0'
    }"
  >
    <div class="modal-body">
      <!-- 标题 -->
      <div class="modal-title">订单尚未完成支付</div>

      <!-- 关闭按钮 -->
      <a-button class="close-btn" @click="handleClose">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </a-button>

      <!-- 内容区域 -->
      <div class="modal-content">
        <!-- 待支付订单标题 -->
        <div class="order-title">待支付订单</div>

        <!-- 订单信息 -->
        <div class="order-info">
          <div class="order-info-row">
            <span class="order-info-label">套餐：</span>
            <span class="order-info-value">{{ packageName }}（{{ seatCount }}）人</span>
          </div>

          <div class="order-info-row">
            <span class="order-info-label">时长：</span>
            <span class="order-info-value">{{ duration }}/{{ durationText }}</span>
          </div>

          <div class="order-info-row">
            <span class="order-info-label">金额：</span>
            <span class="order-info-value">
              ¥{{ originalPrice }}
            </span>
          </div>

          <div class="order-info-row">
            <span class="order-info-label">优惠：</span>
            <span class="order-info-value order-info-value--discount">
              立减 ¥{{ discount }}
            </span>
          </div>

          <div class="order-divider"></div>

          <div class="order-info-row order-info-row--total">
            <span class="order-info-label">实付金额：</span>
            <span class="order-info-value order-info-value--final">
              ¥{{ amount }}
            </span>
          </div>
        </div>

        <!-- 二维码区域 -->
        <div class="qrcode-wrapper">
          <img
            v-if="qrCodeUrl"
            :src="qrCodeUrl"
            alt="支付二维码"
            class="qrcode-image"
          />
          <div v-else class="qrcode-placeholder">
            <div class="qrcode-placeholder-icon">📱</div>
            <div class="qrcode-placeholder-text">支付二维码</div>
          </div>
        </div>

        <!-- 提示文字 -->
        <div class="qrcode-tip">请使用{{ paymentMethod === 'wechat' ? '微信' : '支付宝' }}扫码支付</div>
      </div>
    </div>
  </a-modal>
</template>

<style lang="less">
.team-order-pending-payment-modal {
  .ant-modal-content {
    border-radius: 38.4px;
    border: 1px solid rgba(235, 235, 245, 0.08);
    background: #2a2a2e;
    overflow: hidden;

    .ant-modal-body {
      padding: 0 !important;

      .modal-body {
        position: relative;
        width: 100%;
        padding: 24px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 40px;

        .modal-title {
          width: 100%;
          color: #ffffff;
          font-family: 'PingFang SC', sans-serif;
          font-size: 20px;
          font-weight: 600;
          line-height: 1.4em;
          letter-spacing: -0.03em;
          text-align: center;
        }

        .close-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          padding: 0;
          color: rgba(236, 233, 242, 0.6);
          border: none;
          border-radius: 10px;
          background: transparent;
          backdrop-filter: blur(25px);
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            color: #ffffff;
            background: rgba(235, 235, 245, 0.08);
          }

          svg {
            width: 16px;
            height: 16px;
          }
        }

        .modal-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;

          .order-title {
            width: 100%;
            color: #ffffff;
            font-family: 'PingFang SC', sans-serif;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.428571em;
            text-align: left;
          }

          .order-info {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 16px;
            padding: 20px 16px;
            border-radius: 12px;
            background: rgba(235, 235, 245, 0.04);

            .order-info-row {
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 8px;

              .order-info-label {
                color: rgba(255, 255, 255, 0.6);
                font-family: 'PingFang SC', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 1.4em;
                text-align: left;
              }

              .order-info-value {
                color: #ffffff;
                font-family: 'PingFang SC', sans-serif;
                font-size: 14px;
                font-weight: 600;
                line-height: 1.428571em;
                text-align: left;

                &--discount {
                  color: #ff715f;
                }

                &--final {
                  color: #837afd;
                  font-size: 18px;
                  font-weight: 600;
                  line-height: 1.4em;
                }
              }

              &--total {
                .order-info-label {
                  color: rgba(255, 255, 255, 0.6);
                }
              }
            }

            .order-divider {
              width: 100%;
              height: 1px;
              background: rgba(255, 255, 255, 0.08);
            }
          }

          .qrcode-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: stretch;
            align-items: stretch;
            width: 180px;
            height: 180px;
            padding: 8px;
            border-radius: 24px;
            background: linear-gradient(
              90deg,
              rgba(49, 49, 49, 1) 0%,
              rgba(51, 51, 51, 1) 100%
            );
            border: 1px solid rgba(235, 235, 245, 0.3);

            .qrcode-image {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }

            .qrcode-placeholder {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 12px;
              width: 100%;
              height: 100%;

              .qrcode-placeholder-icon {
                font-size: 64px;
                opacity: 0.3;
              }

              .qrcode-placeholder-text {
                color: rgba(255, 255, 255, 0.45);
                font-family: 'PingFang SC', sans-serif;
                font-size: 14px;
                font-weight: 400;
              }
            }
          }

          .qrcode-tip {
            width: 100%;
            color: rgba(236, 233, 242, 0.9);
            font-family: 'PingFang SC', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.428571em;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>

